import { Session } from 'next-auth';

import { getAppUnifiedConfig } from '@services/config';
import { getLocalDataIfNotExpired } from '@services/storage';

import { FaiType, FaiTypeWithEligibility } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { ELIGIBILITY_STORAGE_KEY, PLAY, TECHNOLOGY } from '@constants/provider';

import {
    EligibilityResults,
    RawEligibilityOffer,
    SavedEligibilityResultType,
    getConfigBySlug,
} from '@eligibility/helpers';

export function updateOffersWithEligibility(faiProducts: FaiType[], eligibleOffers?: RawEligibilityOffer[]): FaiType[] {
    const eligResultOffers =
        eligibleOffers ?? getLocalDataIfNotExpired<SavedEligibilityResultType>(ELIGIBILITY_STORAGE_KEY.RESULTS)?.offers;

    if (!eligResultOffers) {
        return faiProducts;
    }

    return faiProducts.map((plan) => {
        const offer = eligResultOffers.find((offer) => {
            return offer.rangeNg === plan.rangeNg && offer.commercialSeries === plan.commercialSeries;
        });

        if (offer) {
            plan.price = offer.price;
            plan.rentalFees = offer.rental_fees ?? 0;
            plan.speedOption = offer.speedOption;
            if (offer.speed_up && offer.speed_down) {
                plan.upRates = offer.speed_up;
                plan.downRates = offer.speed_down;
            }
        }

        return plan;
    });
}

export function excludeWrongEligibleProducts(products: FaiType[], eligibleGencodes: string[]): FaiType[] {
    const eligibleProducts = products.filter((p) => eligibleGencodes.includes(p.gencode));

    return products.filter((p) => {
        return !eligibleProducts.some((eligP) => {
            return (
                eligP.rangeNg === p.rangeNg &&
                eligP.commercialSeries === p.commercialSeries &&
                eligP.technology === p.technology &&
                eligP.play === p.play &&
                eligP.gencode !== p.gencode
            );
        });
    });
}

function getPriorityOffer(offer1: FaiType, offer2: FaiType): FaiType {
    return offer1.play === PLAY._3P ? offer1 : offer2;
}

export function groupOffers(plans: FaiType[]) {
    return plans.reduce((acc: Record<string, FaiType>, plan) => {
        const key = `${plan.rangeNg}_${plan.commercialSeries}-${plan.technology}`;

        if (!acc[key]) {
            acc[key] = plan;
        } else {
            acc[key] = getPriorityOffer(acc[key], plan);
        }

        return acc;
    }, {});
}

export async function updatePlansWithEligibility(
    plans: FaiTypeWithEligibility[],
    eligibilityResult: EligibilityResults,
): Promise<FaiTypeWithEligibility[]> {
    const eligibleTechnos = Object.keys(eligibilityResult.offers);
    const eligibleGencodes = eligibleTechnos?.flatMap((t) =>
        eligibilityResult.offers[t as TECHNOLOGY]!.map((o) => o.sku),
    );
    plans = excludeWrongEligibleProducts(plans, eligibleGencodes);

    return plans.map((plan) => {
        const techno: TECHNOLOGY = plan.technology;
        const offer = eligibilityResult.offers[techno]?.find((offer) => {
            return offer.rangeNg === plan.rangeNg && offer.commercialSeries === plan.commercialSeries;
        });

        if (!offer) {
            return {
                ...plan,
                isEligible: false,
            };
        }

        return {
            ...plan,
            price: offer.price,
            rentalFees: offer.rental_fees ?? 0,
            isEligible: true,
            speedOption: offer.option,
            upRates: offer.speed_up ? offer.speed_up : plan.upRates,
            downRates: offer.speed_down ? offer.speed_down : plan.downRates,
        };
    });
}

export type HomeZones = Partial<Record<TECHNOLOGY, string>>;

export async function eligibilityContextualisation(
    plans: FaiType[],
    refreshPlans: () => Promise<FaiType[]>,
    setter: (plans: FaiTypeWithEligibility[], idPanierCorbis: string | undefined, homeZone?: HomeZones) => void,
    session: Session | null,
) {
    const journeyConfig = getConfigBySlug('test-eligibilite');
    const eligibilityConfig = getAppUnifiedConfig().eligibility;

    const shouldRefresh = Boolean(session?.user);

    try {
        EligibilityResults.fromLocalStorage(
            {
                eligibilityConfig,
                journeyConfig,
            },
            session?.user as UserType | undefined,
            async (eligibilityResult) => {
                const _plans: FaiTypeWithEligibility[] = shouldRefresh ? await refreshPlans() : Object.values(plans);

                const homeZone: HomeZones = {
                    [TECHNOLOGY.BOX_4G]: eligibilityResult.results.box4g.homeZone,
                    [TECHNOLOGY.BOX_5G]: eligibilityResult.results.box5g.homeZone,
                };

                setter(
                    await updatePlansWithEligibility(_plans, eligibilityResult),
                    eligibilityResult.idPanierCorbis,
                    homeZone,
                );
            },
        );
    } catch {
        EligibilityResults.clear();

        setter(shouldRefresh ? await refreshPlans() : plans, undefined);
    }
}
